import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Search, Package, Layers, Wand2, Check } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'
import { DemoNotice } from '../lib/demo'

// Master products page, grouped by "owning" client group so it's obvious
// which products belong to which brand. Products without an owner appear
// under a "Master (unassigned)" section — generic products any client can
// be given visibility to via the Product Visibility page.
export default function Products() {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ sku: '', name: '', owner_client_group_id: '', image_url: null })
  // Sage lookup state — searches WG Main's products table via sage-lookup Edge Function
  const [sageQuery, setSageQuery] = useState('')
  const [sageResults, setSageResults] = useState(null)
  const [sageLoading, setSageLoading] = useState(false)
  const [sageError, setSageError] = useState('')
  const [sagePickedCode, setSagePickedCode] = useState(null) // just for the visual "picked" state
  const load = async () => {
    if (isDemoMode) return
    setLoading(true)
    const [pRes, gRes] = await Promise.all([
      supabase.from('products')
        .select('id, sku, name, price, unit, pack_size, image_url, archived_at, owner_client_group_id')
        .is('archived_at', null)
        .order('name'),
      supabase.from('client_groups').select('id, name').is('archived_at', null).order('name'),
    ])
    setRows(pRes.data || [])
    setGroups(gRes.data || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const searchSage = async () => {
    const q = sageQuery.trim()
    if (!q) return
    setSageLoading(true)
    setSageError('')
    setSageResults(null)
    try {
      const { data, error } = await supabase.functions.invoke('sage-lookup', { body: { q } })
      if (error) {
        // supabase-js wraps non-2xx errors in a generic "non-2xx status code"
        // message and stashes the real Response in error.context. Peek at
        // the body so the user sees the actual failure (missing secret,
        // WG Main unreachable, etc.) rather than a useless generic string.
        let realMsg = error.message || 'Lookup failed'
        try {
          if (error.context && typeof error.context.json === 'function') {
            const body = await error.context.json()
            if (body?.error) realMsg = body.error
          }
        } catch { /* fallback to generic */ }
        throw new Error(realMsg)
      }
      if (!data?.ok) throw new Error(data?.error || 'Lookup failed')
      setSageResults(data.results || [])
    } catch (err) {
      setSageError(err.message || 'Lookup failed')
    } finally {
      setSageLoading(false)
    }
  }

  // Heuristic: does the Sage code/name look like it belongs to a specific
  // client group? Match against group name AND its first word so
  // "Away Resorts" matches both AW/* and AWAY-branded codes.
  const guessOwnerGroup = (r) => {
    const hay = `${r.code || ''} ${r.name || ''}`.toLowerCase()
    for (const g of groups) {
      const full = g.name.toLowerCase()
      const first = full.split(/\s+/)[0]
      const initials = full.split(/\s+/).map(w => w[0]).join('') // "away resorts" -> "ar"
      // Match if code/name contains the full name, its first word (3+ chars),
      // or an obvious prefix like "AW/" for "Away Resorts". Kept conservative
      // to avoid false positives on very short names.
      if (hay.includes(full)) return g.id
      if (first.length >= 3 && hay.includes(first)) return g.id
      // Prefix match: "AW/" (Away), "MAR" (Marstons), etc — first 2-3 chars uppercased + optional /
      const shortHint = full.slice(0, 3)
      if (shortHint.length >= 3 && (hay.startsWith(shortHint) || hay.startsWith(shortHint + '/'))) return g.id
      // Initials for multi-word groups (rare but useful)
      if (initials.length >= 2 && hay.startsWith(initials + '/')) return g.id
    }
    return ''
  }

  const pickSageResult = (r) => {
    // Sage `code` → our `sku`. Sage `name` → our `name`. Smart-guess the owner
    // group from the code prefix so the user doesn't have to re-pick it.
    // image_url points at the WG Main sage-product-photos bucket; if the file
    // doesn't exist yet the platform's normal "no image" placeholder shows.
    const ownerId = guessOwnerGroup(r)
    setForm(f => ({
      ...f,
      sku: r.code || '',
      name: r.name || '',
      owner_client_group_id: ownerId || f.owner_client_group_id,
      image_url: r.image_url || null,
    }))
    setSagePickedCode(r.code)
    setSageResults(null)
    setSageQuery('')
  }

  const create = async (e) => {
    e.preventDefault()
    if (isDemoMode) return
    const { sku, name, owner_client_group_id, image_url } = form
    if (!sku.trim() || !name.trim()) return
    const { data, error } = await supabase.from('products')
      .insert({
        sku: sku.trim(),
        name: name.trim(),
        owner_client_group_id: owner_client_group_id || null,
        image_url: image_url || null,
      })
      .select()
      .single()
    if (error) return alert(error.message)
    setForm({ sku: '', name: '', owner_client_group_id: '', image_url: null })
    setCreating(false)
    if (data?.id) navigate(`/products/${data.id}`)
  }

  // Group products by owner. "Master (unassigned)" bucket goes last.
  const grouped = useMemo(() => {
    const s = q.toLowerCase()
    const filtered = rows.filter(p =>
      !s || p.name.toLowerCase().includes(s) || (p.sku || '').toLowerCase().includes(s)
    )
    const byOwner = new Map()
    for (const p of filtered) {
      const key = p.owner_client_group_id || '__master__'
      if (!byOwner.has(key)) byOwner.set(key, [])
      byOwner.get(key).push(p)
    }
    // Deterministic order: groups by name (matches sidebar), master bucket last.
    const owned = groups
      .map(g => ({ id: g.id, name: g.name, products: byOwner.get(g.id) || [] }))
      .filter(g => g.products.length > 0)
    const master = { id: '__master__', name: 'Master (unassigned)', products: byOwner.get('__master__') || [] }
    const result = [...owned]
    if (master.products.length > 0) result.push(master)
    return result
  }, [rows, groups, q])

  const totalMatching = grouped.reduce((n, g) => n + g.products.length, 0)

  return (
    <div className="space-y-4">
      <DemoNotice />

      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by name or SKU…"
              className="input-field pl-9"
            />
          </div>
          <button onClick={() => setCreating(c => !c)} className="btn-primary">
            <Plus className="w-4 h-4" /> New Product
          </button>
        </div>

        {creating && (
          <form onSubmit={create} className="mb-4 p-3 bg-gray-50 rounded-lg">
            {/* Sage lookup — searches WG Main via the sage-lookup Edge Function */}
            <div className="mb-3 p-3 rounded-lg bg-white border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Wand2 className="w-4 h-4 text-brand" />
                <span className="text-sm font-medium text-gray-700">Look up in Sage (WG Main)</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Search by product code or name — we'll prefill SKU + name from Sage.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={sageQuery}
                  onChange={e => setSageQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchSage() } }}
                  placeholder="e.g. AW/HMP/01 or Welcome Hamper"
                  className="input-field flex-1"
                />
                <button
                  type="button"
                  onClick={searchSage}
                  disabled={sageLoading || !sageQuery.trim()}
                  className="btn-secondary text-sm whitespace-nowrap"
                >
                  {sageLoading ? 'Searching…' : 'Search Sage'}
                </button>
              </div>
              {sageError && <p className="text-xs text-red-600 mt-2">{sageError}</p>}
              {sageResults && (
                <div className="mt-2 rounded-lg border border-gray-200 bg-white max-h-64 overflow-auto">
                  {sageResults.length === 0 ? (
                    <p className="text-sm text-gray-500 italic p-3">No matches in Sage for "{sageQuery}".</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {sageResults.map(r => {
                        const guessedGroupId = guessOwnerGroup(r)
                        const guessedGroup = guessedGroupId ? groups.find(g => g.id === guessedGroupId) : null
                        return (
                          <li key={r.code}>
                            <button
                              type="button"
                              onClick={() => pickSageResult(r)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3"
                            >
                              {/* Thumbnail from WG Main sage-product-photos bucket.
                                  Renders only if the file exists — onError swaps in a
                                  neutral placeholder so missing photos don't shout. */}
                              {r.image_url ? (
                                <img
                                  src={r.image_url}
                                  alt=""
                                  className="w-12 h-12 rounded-md object-contain bg-gray-50 border border-gray-100 shrink-0"
                                  onError={e => {
                                    e.currentTarget.style.visibility = 'hidden'
                                    e.currentTarget.parentElement?.querySelector('[data-placeholder]')?.removeAttribute('hidden')
                                  }}
                                />
                              ) : null}
                              <div hidden={!!r.image_url} data-placeholder className="w-12 h-12 rounded-md bg-gray-50 border border-gray-100 shrink-0 flex items-center justify-center text-gray-300">
                                <Package className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-800">
                                  {r.code}
                                  {!r.is_active && <span className="ml-2 text-[10px] uppercase font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Inactive</span>}
                                  {r.is_discontinuing && <span className="ml-2 text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Discontinuing</span>}
                                  {guessedGroup && (
                                    <span className="ml-2 text-[10px] uppercase font-bold text-brand-dark bg-brand-50 border border-brand/30 px-1.5 py-0.5 rounded">
                                      → {guessedGroup.name}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-600 truncate">{r.name}</div>
                                <div className="text-[11px] text-gray-400 truncate">
                                  {[r.unit_size, r.supplier, r.category].filter(Boolean).join(' · ')}
                                  {r.unit_cost != null && ` · cost £${Number(r.unit_cost).toFixed(2)}`}
                                </div>
                              </div>
                              <span className="text-xs text-brand-dark shrink-0">Use →</span>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              )}
              {sagePickedCode && (
                <div className="mt-2 flex items-center gap-2 text-xs text-emerald-700">
                  <Check className="w-3.5 h-3.5" />
                  Filled from Sage code <strong>{sagePickedCode}</strong>
                  {form.owner_client_group_id && (
                    <> and auto-assigned to <strong>{groups.find(g => g.id === form.owner_client_group_id)?.name}</strong></>
                  )}. Adjust below if needed.
                </div>
              )}
            </div>

            <p className="text-xs text-gray-500 mb-2">
              Just SKU + name to start — image, description, pack size and price are added on the next screen.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                type="text" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })}
                placeholder="SKU" className="input-field" autoFocus required
              />
              <input
                type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Product name" className="input-field" required
              />
              <select
                value={form.owner_client_group_id}
                onChange={e => setForm({ ...form, owner_client_group_id: e.target.value })}
                className="input-field"
                title="Which client is this product for? Leave blank for a generic/master product."
              >
                <option value="">— Master (any client) —</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2 mt-2">
              <button type="submit" className="btn-primary">Create &amp; edit details</button>
              <button type="button" onClick={() => { setCreating(false); setForm({ sku: '', name: '', owner_client_group_id: '' }) }} className="btn-secondary">Cancel</button>
            </div>
          </form>
        )}

        {loading && <p className="text-sm text-gray-500">Loading…</p>}

        {!loading && totalMatching === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center">
            {rows.length === 0 ? 'No products yet — add one above.' : 'No matches.'}
          </p>
        )}
      </div>

      {grouped.map(group => (
        <div key={group.id} className="card">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-brand" />
            <h3 className="font-semibold text-gray-800">{group.name}</h3>
            <span className="text-xs text-gray-400">· {group.products.length} product{group.products.length === 1 ? '' : 's'}</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {group.products.map(p => (
              <li key={p.id}>
                <Link to={`/products/${p.id}`} className="flex items-center gap-3 py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
                  {p.image_url
                    ? <img src={p.image_url} alt="" className="w-10 h-10 rounded-md object-cover bg-gray-100" />
                    : <div className="w-10 h-10 rounded-md bg-brand-50 flex items-center justify-center"><Package className="w-5 h-5 text-brand" /></div>
                  }
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 truncate">{p.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {p.sku}{p.unit ? ` · ${p.unit}` : ''}{p.pack_size ? ` · ${p.pack_size}/box` : ''}
                    </div>
                  </div>
                  {p.price != null && (
                    <div className="text-sm font-medium text-gray-700 shrink-0">£{Number(p.price).toFixed(2)}</div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
